import { createSelector } from 'reselect'
import { sourcesDataSelector, limitSelector } from './RouteSelectors'
import { bookingsSelector } from './BookingSelectors'
import { getSum } from 'services/helpers'
import compose from 'ramda/es/compose'
import map from 'ramda/es/map'
import reduce from 'ramda/es/reduce'
import flatten from 'ramda/es/flatten'
import values from 'ramda/es/values'

const getState = state => state.source
const sourcesSelector = state => getState(state).sources
const monthSourcesData = state => getState(state).monthSourcesData

const mergedSourcesSelector = createSelector(
  sourcesSelector,
  sourcesDataSelector,
  (sources, sourcesData) => sources.map(source => {
    const data = sourcesData.find(({ id }) => id === source.id)

    return {
      ...source,
      ...data
    }
  })
)

const withoutBusStationsSelector = createSelector(
  mergedSourcesSelector,
  sources => sources.filter(({ id }) => {
    const splitedId = id.split('_')

    return splitedId[1] !== 'busStation'
  })
)

const sheetBookingsSelector = createSelector(
  bookingsSelector,
  bookings => bookings.filter(booking => booking.source !== 'system' && booking.status !== 'blacklist') || []
)

const sheetLimitSelector = createSelector(
  limitSelector,
  sheetBookingsSelector,
  (limit, sheetBookings) => +limit + sheetBookings.length
)

const totalLimitSelector = createSelector(
  sheetLimitSelector,
  mergedSourcesSelector,
  (sheetLimit, sources) => sources.reduce((total, source) => total + +source.limit, 0) + sheetLimit
)

const bookedSelector = createSelector(
  sheetBookingsSelector,
  mergedSourcesSelector,
  (sheetBookings, sources) =>
    sources.reduce((total, source) => total + (+source.booked || 0), 0) + sheetBookings.length
)

const monthSourcesDataSelector = createSelector(
  monthSourcesData,
  sources => (
    sources.map(source => {
      const { limit, totalSheetBookings, sourcesData } = source

      const tsb = +totalSheetBookings || 0
      const tsbwl = +limit + tsb || 10

      const totalLimit = [tsbwl]
      const totalBookings = [tsb]

      sourcesData.forEach(({ booked = 0, limit }) => {
        totalBookings.push(+booked)
        totalLimit.push(+limit)
      })

      return {
        tsb,
        tsbwl,
        totalLimit: totalLimit.reduce(getSum, 0),
        totalBookings: totalBookings.reduce(getSum, 0),
        ...source
      }
    })
  )
)

const getSourceData = ({ sourcesData, totalLimit, totalBookings, tsb, tsbwl }) => ([
  {
    id: 'sheet',
    booked: tsb,
    limit: +tsbwl,
  },
  ...sourcesData,
  {
    id: 'total',
    limit: +totalLimit,
    booked: totalBookings,
  },
])

const reduceSourceData = (acc, { id, booked = 0, limit = 0 }) => {
  const updatedLimit = acc[id]
    ? +acc[id].limit + +limit
    : limit
  const updatedBooked = acc[id]
    ? acc[id].booked + booked
    : booked

  acc[id] = {
    id,
    limit: +updatedLimit,
    booked: updatedBooked,
  }

  return acc
}

const totalSourceMonthSelector = createSelector(
  monthSourcesDataSelector,
  monthSourcesData => {
    const transform = compose(
      values,
      reduce(reduceSourceData, {}),
      flatten,
      map(getSourceData),
    )

    return transform(monthSourcesData)
  }
)

const totalPercentSelector = createSelector(
  totalSourceMonthSelector,
  totalSourceMonth => {
    const total = totalSourceMonth.find(({ id }) => id === 'total')

    return totalSourceMonth.map(({ booked }) => {
      const value = (booked / total.booked * 100).toFixed(0)

      return isNaN(value) ? 0 : value
    })
  }
)

export {
  totalLimitSelector,
  sourcesSelector,
  mergedSourcesSelector,
  sheetBookingsSelector,
  bookedSelector,
  totalSourceMonthSelector,
  sheetLimitSelector,
  withoutBusStationsSelector,
  monthSourcesDataSelector,
  totalPercentSelector
}



// WEBPACK FOOTER //
// ./src/reduxModules/selectors/SourceSelectors.js